/**
 * Centralized API endpoint constants.
 *
 * These paths do NOT include the `/api` prefix — `lib/api.ts`'s `apiFetch`
 * auto-prepends it.  They also do NOT start with `/` so that apiFetch's
 * `endpoint.startsWith('/')` check correctly prepends `/api/`.
 *
 * For raw `fetch()` calls (e.g. AuthProvider) use full `/api/...` paths directly.
 */

type Id = number | string

export const API = {
  // ── Auth (used by AuthProvider via raw fetch — full paths there) ─────
  auth: {
    login: 'auth/login/',
    refresh: 'auth/refresh/',
    logout: 'auth/logout/',
    me: 'auth/me/',
  },

  // ── Patients ────────────────────────────────────────────────────────
  patients: {
    list: 'patients/',
    detail: (id: Id) => `patients/${id}/`,
    search: 'patients/search/',
    // D-3 Patient Archive Endpoint
    archive: (id: Id) => `patients/${id}/archive/`,
    unarchive: (id: Id) => `patients/${id}/unarchive/`,
    archiveHistory: (id: Id) => `patients/${id}/archive-history/`,
    documents: (patientId: Id) => `patients/${patientId}/documents/`,
    documentDetail: (patientId: Id, docId: Id) => `patients/${patientId}/documents/${docId}/`,
    prescriptions: (patientId: Id) => `patients/${patientId}/prescriptions/`,
    prescriptionDetail: (patientId: Id, rxId: Id) => `patients/${patientId}/prescriptions/${rxId}/`,
    prescriptionItems: (patientId: Id, rxId: Id) => `patients/${patientId}/prescriptions/${rxId}/items/`,
    prescriptionItemDetail: (patientId: Id, rxId: Id, itemId: Id) => `patients/${patientId}/prescriptions/${rxId}/items/${itemId}/`,
    prescriptionHistory: (patientId: Id) => `patients/${patientId}/prescription-history/`,
    allergies: (patientId: Id) => `patients/${patientId}/allergies/`,
    allergyDetail: (patientId: Id, allergyId: Id) => `patients/${patientId}/allergies/${allergyId}/`,
    chronicConditions: (patientId: Id) => `patients/${patientId}/chronic-conditions/`,
    chronicConditionDetail: (patientId: Id, ccId: Id) => `patients/${patientId}/chronic-conditions/${ccId}/`,
    pdf: (patientId: Id) => `patients/${patientId}/pdf/`,
    vaccinations: (patientId: Id) => `patients/${patientId}/vaccinations/`,
    vaccinationDetail: (patientId: Id, vaxId: Id) => `patients/${patientId}/vaccinations/${vaxId}/`,
    family: (patientId: Id) => `patients/${patientId}/family/`,
    familyMember: (patientId: Id, memberId: Id) => `patients/${patientId}/family/${memberId}/`,
    monthlyReport: (patientId: Id) => `patients/${patientId}/monthly-report/`,
  },

  // ── Appointments ────────────────────────────────────────────────────
  appointments: {
    list: 'appointments/',
    detail: (id: Id) => `appointments/${id}/`,
    status: (id: Id) => `appointments/${id}/status/`,
    markNoShow: (id: Id) => `appointments/${id}/mark-no-show/`,
    // E-4 archive endpoints
    archive: (id: Id) => `appointments/${id}/archive/`,
    unarchive: (id: Id) => `appointments/${id}/unarchive/`,
    archiveHistory: (id: Id) => `appointments/${id}/archive-history/`,
    conflicts: 'appointments/conflicts/',
    appointmentConflicts: (id: Id) => `appointments/${id}/conflicts/`,
    suggest: 'appointments/suggest/',
    availableSlots: 'appointments/available-slots/',
    doctors: 'appointments/doctors/',
    videoRoom: (id: Id) => `appointments/${id}/video-room/`,
  },
  appointmentTypes: {
    list: 'appointment-types/',
    detail: (id: Id) => `appointment-types/${id}/`,
  },
  conflicts: {
    today: 'conflicts/today/',
  },

  // ── Operations ──────────────────────────────────────────────────────
  operations: {
    list: 'operations/',
    detail: (id: Id) => `operations/${id}/`,
    suggest: 'operations/suggest/',
    checkConflicts: 'operations/check-conflicts/',
    generateInvoice: (opId: Id) => `operations/${opId}/generate-invoice/`,
    byPatient: (patientId: Id) => `patients/${patientId}/operations/`,
  },
  operationTypes: { list: 'operation-types/' },
  opDashboard: {
    list: 'op-dashboard/',
    live: 'op-dashboard/live/',
    status: (id: Id) => `op-dashboard/${id}/status/`,
  },
  opTeam: {
    list: (opId: Id) => `operations/${opId}/team/`,
    delete: (opId: Id, memberId: Id) => `operations/${opId}/team/${memberId}/`,
  },
  opChecklist: {
    detail: (opId: Id) => `operations/${opId}/pre-op-checklist/`,
    complete: (opId: Id) => `operations/${opId}/pre-op-checklist/complete/`,
  },
  opReport: {
    detail: (opId: Id) => `operations/${opId}/report/`,
    sign: (opId: Id) => `operations/${opId}/report/sign/`,
    pdf: (opId: Id) => `operations/${opId}/report/pdf/`,
    dischargeLetter: (opId: Id) => `operations/${opId}/discharge-letter/`,
  },
  opPostNotes: {
    list: (opId: Id) => `operations/${opId}/post-op-notes/`,
    detail: (noteId: Id) => `post-op-notes/${noteId}/`,
  },
  opStats: { overview: 'op-stats/overview/' },
  opTimeline: { list: 'op-timeline/' },
  opStaff: { list: 'op-staff/' },

  // ── ICD-10-CM catalog (global; used by the operation modals) ─────────
  icd10: { search: 'icd10/search/' },

  // ── MOHAP product catalog (global; billing + prescription search) ────
  catalog: { search: 'catalog/search/' },

  // ── Patient Flow ────────────────────────────────────────────────────
  patientFlow: {
    list: 'patient-flow/',
    detail: (id: Id) => `patient-flow/${id}/`,
    call: (id: Id) => `patient-flow/${id}/call/`,
    status: (id: Id) => `patient-flow/${id}/status/`,
    live: 'patient-flow/live/',
    waitingRoom: 'patient-flow/waiting-room/',
    nextNumber: 'patient-flow/next-number/',
    waitEstimate: 'patient-flow/wait-estimate/',
  },

  // ── Doctors & Scheduling ────────────────────────────────────────────
  doctors: {
    list: 'doctors/',
    detail: (id: Id) => `doctors/${id}/`,
    stats: 'doctors/stats/',
    // E-3 Doctor archive endpoints (parity with patients.archive*)
    archive: (id: Id) => `doctors/${id}/archive/`,
    unarchive: (id: Id) => `doctors/${id}/unarchive/`,
    archiveHistory: (id: Id) => `doctors/${id}/archive-history/`,
  },
  doctorHours: {
    list: 'doctor-hours/',
    detail: (id: Id) => `doctor-hours/${id}/`,
  },
  doctorAbsences: {
    list: 'doctor-absences/',
    detail: (id: Id) => `doctor-absences/${id}/`,
    preview: 'doctor-absences/preview/',
  },
  doctorBreaks: { list: 'doctor-breaks/' },
  practiceHours: {
    list: 'practice-hours/',
    detail: (id: Id) => `practice-hours/${id}/`,
  },
  availability: { list: 'availability/' },
  calendar: {
    day: 'calendar/day/',
    week: 'calendar/week/',
    month: 'calendar/month/',
  },

  // ── Resources ───────────────────────────────────────────────────────
  resources: {
    list: 'resources/',
    detail: (id: Id) => `resources/${id}/`,
    availability: 'resources/availability/',
  },

  // ── Invoices & Billing ──────────────────────────────────────────────
  invoices: {
    list: 'invoices/',
    detail: (id: Id) => `invoices/${id}/`,
    addItem: (id: Id) => `invoices/${id}/add-item/`,
    addPayment: (id: Id) => `invoices/${id}/add-payment/`,
    issue: (id: Id) => `invoices/${id}/issue/`,
    cancel: (id: Id) => `invoices/${id}/cancel/`,
    pdf: (id: Id) => `invoices/${id}/pdf/`,
    sendEmail: (id: Id) => `invoices/${id}/send-email/`,
    sendReminder: (id: Id) => `invoices/${id}/send-reminder/`,
    convertEstimate: (id: Id) => `invoices/${id}/convert-estimate/`,
    createCreditNote: (id: Id) => `invoices/${id}/create-credit-note/`,
  },
  invoiceItems: {
    list: 'invoice-items/',
    detail: (id: Id) => `invoice-items/${id}/`,
  },
  payments: { list: 'payments/' },
  billingAnalytics: {
    kpis: 'billing/analytics/kpis/',
    revenueMonthly: 'billing/analytics/revenue/monthly/',
    openInvoices: 'billing/analytics/open-invoices/',
    overdueInvoices: 'billing/analytics/overdue-invoices/',
    dashboard: 'billing/analytics/dashboard/',
  },
  cashRegister: {
    info: 'cash-register/',
    transactions: 'cash-register/transactions/',
    dailyClosings: 'cash-register/daily-closings/',
    summary: 'cash-register/summary/',
  },
  taxRates: {
    list: 'tax-rates/',
    detail: (id: Id) => `tax-rates/${id}/`,
  },
  discounts: {
    list: 'discounts/',
    detail: (id: Id) => `discounts/${id}/`,
  },
  servicePackages: {
    list: 'service-packages/',
    detail: (id: Id) => `service-packages/${id}/`,
    addItem: (id: Id) => `service-packages/${id}/add-item/`,
    removeItem: (id: Id, itemId: Id) => `service-packages/${id}/remove-item/${itemId}/`,
  },
  patientPackages: {
    list: 'patient-packages/',
    detail: (id: Id) => `patient-packages/${id}/`,
    useSession: (id: Id) => `patient-packages/${id}/use-session/`,
  },
  paymentPlans: {
    list: 'payment-plans/',
    detail: (id: Id) => `payment-plans/${id}/`,
    checkOverdue: (id: Id) => `payment-plans/${id}/check-overdue/`,
  },
  installments: {
    list: 'payment-plan-installments/',
    detail: (id: Id) => `payment-plan-installments/${id}/`,
    pay: (id: Id) => `payment-plan-installments/${id}/pay/`,
  },
  commissionRules: {
    list: 'commission-rules/',
    detail: (id: Id) => `commission-rules/${id}/`,
  },
  doctorCommissions: {
    list: 'doctor-commissions/',
    detail: (id: Id) => `doctor-commissions/${id}/`,
    approve: (id: Id) => `doctor-commissions/${id}/approve/`,
    markPaid: (id: Id) => `doctor-commissions/${id}/mark-paid/`,
  },
  exchangeRates: {
    list: 'exchange-rates/',
    detail: (id: Id) => `exchange-rates/${id}/`,
  },
  billingReports: {
    report: 'billing/reports/',
  },
  // Sprint R Phase R4: Klinik-Ausgaben (Owner-Sicht).
  expenses: {
    list: 'expenses/',
    detail: (id: Id) => `expenses/${id}/`,
  },
  // Sprint R Phase R6: Owner-Dashboard analytics endpoints.
  ownerDashboard: {
    expenses: 'billing/analytics/expenses/',
    profitability: 'billing/analytics/profitability/',
    revenueByDoctor: 'billing/reports/?group_by=doctor',
    kpis: 'billing/analytics/kpis/',
  },

  // ── Services ────────────────────────────────────────────────────────
  services: {
    list: 'services/',
    detail: (id: Id) => `services/${id}/`,
    deactivate: (id: Id) => `services/${id}/deactivate/`,
    activate: (id: Id) => `services/${id}/activate/`,
  },

  // ── Todos ───────────────────────────────────────────────────────────
  todos: {
    list: 'todos/',
    detail: (id: Id) => `todos/${id}/`,
    insights: 'todos/insights/',
    board: 'todos/board/',
    move: (id: Id) => `todos/${id}/move/`,
    comments: (id: Id) => `todos/${id}/comments/`,
    commentDetail: (todoId: Id, commentId: Id) => `todos/${todoId}/comments/${commentId}/`,
    subtasks: (id: Id) => `todos/${id}/subtasks/`,
    subtaskDetail: (todoId: Id, subtaskId: Id) => `todos/${todoId}/subtasks/${subtaskId}/`,
    tags: (id: Id) => `todos/${id}/tags/`,
    attachments: (id: Id) => `todos/${id}/attachments/`,
    attachmentDetail: (todoId: Id, attachmentId: Id) => `todos/${todoId}/attachments/${attachmentId}/`,
  },
  tags: { list: 'tags/' },
  boardViews: {
    list: 'board-views/',
    detail: (id: Id) => `board-views/${id}/`,
  },
  teams: { list: 'teams/' },

  // ── Users & Roles ──────────────────────────────────────────────────
  users: {
    list: 'users/',
    detail: (id: Id) => `users/${id}/`,
  },
  roles: { list: 'roles/' },

  // ── Tenants ─────────────────────────────────────────────────────────
  tenants: {
    list: 'tenants/',
    current: 'tenants/current/',
    detail: (id: Id) => `tenants/${id}/`,
  },

  // ── Feature Flags ───────────────────────────────────────────────────
  featureFlags: {
    list: 'feature-flags/',
    detail: (id: Id) => `feature-flags/${id}/`,
  },
  features: {
    list: 'features/',
    unregistered: 'features/unregistered/',
    tenants: (id: Id) => `features/${id}/tenants/`,
    sync: 'features/sync/',
  },

  // ── Emergency / Notaufnahme (ER) ────────────────────────────────────
  emergency: {
    visits: 'emergency-visits/',
    visit: (id: Id) => `emergency-visits/${id}/`,
    // Wristband print is a POST (mutates band_print_count) that returns a PDF.
    wristband: (id: Id) => `emergency-visits/${id}/wristband/`,
    board: 'emergency-visits/board/',
    beds: 'emergency-beds/',
    orders: 'emergency-orders/',
    orderDispense: (id: Id) => `emergency-orders/${id}/dispense/`,
    invoice: (id: Id) => `emergency-visits/${id}/invoice/`,
    admit: (id: Id) => `emergency-visits/${id}/admit/`,
    lookup: 'emergency-visits/lookup/',
    identify: (id: Id) => `emergency-visits/${id}/identify/`,
  },

  // ── Facility admin (departments / beds) — RBAC: facility.* ───────────
  facility: {
    departments: 'facility-departments/',
    department: (id: Id) => `facility-departments/${id}/`,
    beds: 'facility-beds/',
    bed: (id: Id) => `facility-beds/${id}/`,
  },

  // ── Inpatient (IPD) ─────────────────────────────────────────────────
  encounters: {
    list: 'encounters/',
    detail: (id: Id) => `encounters/${id}/`,
    invoice: (id: Id) => `encounters/${id}/invoice/`,
  },

  // ── Translations ────────────────────────────────────────────────────
  translations: {
    list: 'translations/',
    detail: (id: Id) => `translations/${id}/`,
  },

  // ── Medical Records ─────────────────────────────────────────────────
  medicalRecords: {
    list: 'medical-records/',
    detail: (id: Id) => `medical-records/${id}/`,
    sign: (id: Id) => `medical-records/${id}/sign/`,
    timeline: (id: Id) => `medical-records/${id}/timeline/`,
    diagnoses: (id: Id) => `medical-records/${id}/diagnoses/`,
    diagnosisDetail: (recordId: Id, diagId: Id) => `medical-records/${recordId}/diagnoses/${diagId}/`,
    vitalSigns: (id: Id) => `medical-records/${id}/vital-signs/`,
    vitalSignDetail: (recordId: Id, vsId: Id) => `medical-records/${recordId}/vital-signs/${vsId}/`,
    pdf: (id: Id) => `medical-records/${id}/pdf/`,
  },

  // ── Prescriptions (legacy — PatientPrescription) ───────────────────
  prescriptions: {
    list: 'prescriptions/',
    detail: (id: Id) => `prescriptions/${id}/`,
    pdf: (id: Id) => `prescriptions/${id}/pdf/`,
    duplicate: (id: Id) => `prescriptions/${id}/duplicate/`,
    updateStatus: (id: Id) => `prescriptions/${id}/update-status/`,
    checkInteractions: 'prescriptions/check-interactions/',
    checkDrugInteractions: 'prescriptions/check-drug-interactions/',
    calculateDosage: 'prescriptions/calculate-dosage/',
  },
  prescriptionTemplates: {
    list: 'prescription-templates/',
    detail: (id: Id) => `prescription-templates/${id}/`,
    apply: (id: Id) => `prescription-templates/${id}/apply/`,
  },

  // ── Medications (PROMPT 16) ───────────────────────────────────────
  medications: {
    list: 'medications/',
    detail: (id: Id) => `medications/${id}/`,
    search: 'medications/search/',
  },

  // ── New Prescriptions (PROMPT 16) ─────────────────────────────────
  rx: {
    list: 'rx/',
    detail: (id: Id) => `rx/${id}/`,
    issue: (id: Id) => `rx/${id}/issue/`,
    dispense: (id: Id) => `rx/${id}/dispense/`,
    cancel: (id: Id) => `rx/${id}/cancel/`,
    duplicate: (id: Id) => `rx/${id}/duplicate/`,
    pdf: (id: Id) => `rx/${id}/pdf/`,
  },

  // ── Repeat Prescriptions (PROMPT 16) ──────────────────────────────
  repeatPrescriptions: {
    list: 'repeat-prescriptions/',
    detail: (id: Id) => `repeat-prescriptions/${id}/`,
    refill: (id: Id) => `repeat-prescriptions/${id}/refill/`,
    pause: (id: Id) => `repeat-prescriptions/${id}/pause/`,
    resume: (id: Id) => `repeat-prescriptions/${id}/resume/`,
    cancel: (id: Id) => `repeat-prescriptions/${id}/cancel/`,
  },

  // ── Lab ────────────────────────────────────────────────────────────
  lab: {
    catalog: 'lab/catalog/',
    catalogDetail: (id: Id) => `lab/catalog/${id}/`,
    catalogByCategory: 'lab/catalog/by-category/',
    seedDefaults: 'lab/catalog/seed-defaults/',
    panels: 'lab/panels/',
    panelDetail: (id: Id) => `lab/panels/${id}/`,
    orders: 'lab/orders/',
    orderDetail: (id: Id) => `lab/orders/${id}/`,
    orderStatus: (id: Id) => `lab/orders/${id}/update-status/`,
    orderCollectSample: (id: Id) => `lab/orders/${id}/collect-sample/`,
    orderCancel: (id: Id) => `lab/orders/${id}/cancel/`,
    orderBulkEnter: (id: Id) => `lab/orders/${id}/bulk-enter/`,
    orderUploadResult: (id: Id) => `lab/orders/${id}/upload-result/`,
    orderPdf: (id: Id) => `lab/orders/${id}/pdf/`,
    results: (orderId: Id) => `lab/orders/${orderId}/results/`,
    resultDetail: (orderId: Id, resultId: Id) => `lab/orders/${orderId}/results/${resultId}/`,
    resultVerify: (orderId: Id, resultId: Id) => `lab/orders/${orderId}/results/${resultId}/verify/`,
    patientHistory: (patientId: Id) => `patients/${patientId}/lab-history/`,
    patientTrends: (patientId: Id) => `patients/${patientId}/lab-trends/`,
  },

  // ── Imaging ────────────────────────────────────────────────────────
  imaging: {
    studies: 'imaging/studies/',
    types: 'imaging/types/',
    studyDetail: (id: Id) => `imaging/studies/${id}/`,
    studyUploadResult: (id: Id) => `imaging/studies/${id}/upload-result/`,
    reportPdf: (id: Id) => `imaging/studies/${id}/report-pdf/`,
    images: (studyId: Id) => `imaging/studies/${studyId}/images/`,
    imageDetail: (studyId: Id, imageId: Id) => `imaging/studies/${studyId}/images/${imageId}/`,
    reports: (studyId: Id) => `imaging/studies/${studyId}/reports/`,
    reportDetail: (studyId: Id, reportId: Id) => `imaging/studies/${studyId}/reports/${reportId}/`,
    reportSign: (studyId: Id, reportId: Id) => `imaging/studies/${studyId}/reports/${reportId}/sign/`,
    patientHistory: (patientId: Id) => `patients/${patientId}/imaging-history/`,
  },

  // ── Messaging ───────────────────────────────────────────────────────
  messages: {
    list: 'messages/',
    detail: (id: Id) => `messages/${id}/`,
    cancel: (id: Id) => `messages/${id}/cancel/`,
    sendNow: (id: Id) => `messages/${id}/send-now/`,
  },
  messageTemplates: {
    list: 'message-templates/',
    detail: (id: Id) => `message-templates/${id}/`,
    preview: (id: Id) => `message-templates/${id}/preview/`,
  },
  smsProvider: 'sms-provider/',
  whatsappProvider: 'whatsapp-provider/',
  connectionStatus: 'connection-status/',

  // ── Internal Chat + Bulk + Stats ────────────────────────────────
  conversations: {
    list: 'messaging/conversations/',
    detail: (id: Id) => `messaging/conversations/${id}/`,
    markRead: (id: Id) => `messaging/conversations/${id}/read/`,
    sendMessage: (id: Id) => `messaging/conversations/${id}/messages/`,
    direct: 'messaging/conversations/direct/',
  },
  messagingBulkSend: 'messaging/send-bulk/',
  messagingStats: 'messaging/stats/',
  messagingUnreadCount: 'messaging/unread-count/',
  internalMessage: (id: Id) => `messaging/messages/${id}/`,
  patientMessages: (patientId: Id) => `patients/${patientId}/messages/`,

  // ── Medical Reports ─────────────────────────────────────────────────
  medicalReports: {
    list: 'medical-reports/',
    detail: (id: Id) => `medical-reports/${id}/`,
    sign: (id: Id) => `medical-reports/${id}/sign/`,
    pdf: (id: Id) => `medical-reports/${id}/pdf/`,
    send: (id: Id) => `medical-reports/${id}/send/`,
  },

  // ── Medical Report Templates ───────────────────────────────────────
  reportTemplates: {
    list: 'report-templates/',
    detail: (id: Id) => `report-templates/${id}/`,
    render: 'report-templates/render/',
    preview: (id: Id) => `report-templates/${id}/preview/`,
    placeholders: 'report-templates/placeholders/',
  },

  // ── Waiting Display ───────────────────────────────────────────────
  waitingDisplay: {
    config: 'waiting-display/config/',
    configDetail: (id: Id) => `waiting-display/config/${id}/`,
    call: 'waiting-display/call/',
    callUpdate: (id: Id) => `waiting-display/call/${id}/`,
    queue: 'waiting-display/queue/',
    public: (token: string) => `waiting-display/public/${token}/`,
  },

  // ── Recall ────────────────────────────────────────────────────────
  recall: {
    templates: 'recalls/templates/',
    templateDetail: (id: Id) => `recalls/templates/${id}/`,
    schedules: 'recalls/schedules/',
    scheduleDetail: (id: Id) => `recalls/schedules/${id}/`,
    scheduleSend: (id: Id) => `recalls/schedules/${id}/send-now/`,
    dueToday: 'recalls/schedules/due-today/',
    stats: 'recalls/stats/',
    bulkCreate: 'recalls/bulk-create/',
    patientRecalls: (patientId: Id) => `recalls/patients/${patientId}/recalls/`,
  },

  // ── Scheduling (Shifts) ───────────────────────────────────────────
  shifts: {
    templates: 'scheduling/templates/',
    templateDetail: (id: Id) => `scheduling/templates/${id}/`,
    list: 'scheduling/shifts/',
    detail: (id: Id) => `scheduling/shifts/${id}/`,
    week: 'scheduling/shifts/week/',
    myShifts: 'scheduling/shifts/my-shifts/',
    bulkCreate: 'scheduling/shifts/bulk-create/',
    stats: 'scheduling/stats/',
    swapRequests: 'scheduling/swap-requests/',
    swapDetail: (id: Id) => `scheduling/swap-requests/${id}/`,
    swapApprove: (id: Id) => `scheduling/swap-requests/${id}/approve/`,
    swapReject: (id: Id) => `scheduling/swap-requests/${id}/reject/`,
  },

  // ── Triage ────────────────────────────────────────────────────────
  triage: {
    questions: 'triage/questions/',
    questionDetail: (id: Id) => `triage/questions/${id}/`,
    templates: 'triage/templates/',
    templateDetail: (id: Id) => `triage/templates/${id}/`,
    assessments: 'triage/assessments/',
    assessmentDetail: (id: Id) => `triage/assessments/${id}/`,
    assessmentComplete: (id: Id) => `triage/assessments/${id}/complete/`,
    assessmentReview: (id: Id) => `triage/assessments/${id}/review/`,
    patientTriage: (patientId: Id) => `patients/${patientId}/triage/`,
    assessmentPdf: (id: Id) => `triage/assessments/${id}/pdf/`,
    riskAssessmentSources: 'triage/risk-assessment/sources/',
  },

  // ── Pharmacy ──────────────────────────────────────────────────────
  pharmacy: {
    items: 'pharmacy/items/',
    itemDetail: (id: Id) => `pharmacy/items/${id}/`,
    lowStock: 'pharmacy/items/low-stock/',
    expiringSoon: 'pharmacy/items/expiring-soon/',
    batches: (itemId: Id) => `pharmacy/items/${itemId}/batches/`,
    transactions: 'pharmacy/transactions/',
    itemHistory: (itemId: Id) => `pharmacy/items/${itemId}/history/`,
    sales: 'pharmacy/sales/',
    saleCreate: 'pharmacy/sales/create/',
    inventory: 'pharmacy/inventory/',
    inventoryValue: 'pharmacy/inventory/value/',
    movementReport: 'pharmacy/reports/movements/',
  },

  // ── Notifications ────────────────────────────────────────────────
  notifications: {
    list: 'notifications/',
    unreadCount: 'notifications/unread-count/',
    readAll: 'notifications/read-all/',
    markRead: (id: Id) => `notifications/${id}/read/`,
    delete: (id: Id) => `notifications/${id}/`,
  },

  // ── Audit Logs ──────────────────────────────────────────────────
  auditLogs: {
    list: 'audit-logs/',
    actions: 'audit-logs/actions/',
    stats: 'audit-logs/stats/',
  },

  // ── Import / Export ─────────────────────────────────────────────
  dataImport: {
    validate: 'import/validate/',
    execute: 'import/execute/',
    logs: 'import/logs/',
    template: (type: string) => `import/templates/${type}/`,
  },
  dataExport: {
    create: 'export/',
    download: (id: Id) => `export/download/${id}/`,
    logs: 'export/logs/',
  },

  // ── Dashboard Config ────────────────────────────────────────────
  dashboardConfig: {
    get: 'dashboard/config/',
    save: 'dashboard/config/',
    reset: 'dashboard/config/reset/',
  },

  // ── Print Templates ─────────────────────────────────────────────
  printTemplates: {
    list: 'print-templates/',
    detail: (type: string) => `print-templates/${type}/`,
    preview: (type: string) => `print-templates/${type}/preview/`,
  },

  // ── Clinic Documents ────────────────────────────────────────────
  documentFolders: {
    list: 'document-folders/',
    detail: (id: Id) => `document-folders/${id}/`,
  },
  clinicDocuments: {
    list: 'clinic-documents/',
    detail: (id: Id) => `clinic-documents/${id}/`,
  },
  documentStats: 'document-stats/',

  // ── AI ─────────────────────────────────────────────────────────
  ai: {
    status: 'ai/status/',
  },

  // ── Patient Portal ─────────────────────────────────────────────
  portal: {
    auth: 'portal/auth/',
    profile: 'portal/profile/',
    appointments: 'portal/appointments/',
    createAccess: 'portal/access/',
  },

  // ── Feedback (in-app tickets) ──────────────────────────────────
  feedback: {
    tickets: 'feedback/tickets/',
    ticket: (id: Id) => `feedback/tickets/${id}/`,
    comments: (id: Id) => `feedback/tickets/${id}/comments/`,
    attachments: (id: Id) => `feedback/tickets/${id}/attachments/`,
    promote: (id: Id) => `feedback/tickets/${id}/promote/`,
  },

  // ── Accounting (P1-GL: UAS general ledger) ─────────────────────
  accounting: {
    accounts: 'accounting/accounts/',
    account: (id: Id) => `accounting/accounts/${id}/`,
    accountDeactivate: (id: Id) => `accounting/accounts/${id}/deactivate/`,
    accountReactivate: (id: Id) => `accounting/accounts/${id}/reactivate/`,
    chartTemplates: 'accounting/chart-templates/',
    chartSeed: 'accounting/chart-templates/seed/',
    // journal (P1-GL slice 3/4 + B1 draft editing)
    journalEntries: 'accounting/journal-entries/',
    journalEntry: (id: Id) => `accounting/journal-entries/${id}/`,
    journalDraft: 'accounting/journal-entries/draft/',
    journalLines: (id: Id) => `accounting/journal-entries/${id}/lines/`,
    journalLine: (id: Id, lineId: Id) => `accounting/journal-entries/${id}/lines/${lineId}/`,
    journalPost: (id: Id) => `accounting/journal-entries/${id}/post/`,
    journalReverse: (id: Id) => `accounting/journal-entries/${id}/reverse/`,
    fiscalYears: 'accounting/fiscal-years/',
    // posting rules + unposted queue (P1-GL slice 4)
    postingRules: 'accounting/posting-rules/',
    postingRule: (id: Id) => `accounting/posting-rules/${id}/`,
    postingRuleDeactivate: (id: Id) => `accounting/posting-rules/${id}/deactivate/`,
    unpostedEvents: 'accounting/unposted-events/',
    unpostedEvent: (id: Id) => `accounting/unposted-events/${id}/`,
    unpostedRedrive: (id: Id) => `accounting/unposted-events/${id}/redrive/`,
    // reports, fiscal calendar, opening balance (P1-GL slices 2 + 5)
    trialBalance: 'accounting/reports/trial-balance/',
    trialBalanceCsv: 'accounting/reports/trial-balance/csv/',
    trialBalanceDrill: 'accounting/reports/trial-balance/drill/',
    fiscalPeriods: 'accounting/fiscal-periods/',
    fiscalPeriodClose: (id: Id) => `accounting/fiscal-periods/${id}/close/`,
    fiscalPeriodReopen: (id: Id) => `accounting/fiscal-periods/${id}/reopen/`,
    fiscalPeriodFinallyClose: (id: Id) => `accounting/fiscal-periods/${id}/finally-close/`,
    openingBalance: 'accounting/opening-balance/',
    openingBalancePost: (id: Id) => `accounting/opening-balance/${id}/post/`,
  },
} as const
